import "core-js/fn/array/fill"; // note: required to get working editor in ie11

import { DraftHandleValue, EditorState } from "draft-js";
import "draft-js/dist/Draft.css";
import * as React from "react";
import {} from "react";
import { useSelector } from "react-redux";
import { Team, User } from "~/normalizr/entities";
import { AdvancedEditor, AdvancedEditorProps } from "~/sharedComponents/advancedEditor/AdvancedEditor";
import { useKnockoutObservable } from "~/sharedComponents/hooks/useKnockoutObservable";
import { useAICompletion } from "../ai";
import { useHandleKeyCommand } from "../ai/components/useHandleKeyCommand";
import { useMessageContext, useProjectOrTask } from "../messageContext";
import { getMentions, getTeamMentions } from "../selectors/mentions";
import { RootMessageBoardState } from "../types";
import { handleBeforeInput, insertMentions } from "../utils";

export type MessageEditorProps = AdvancedEditorProps & { mentions: User[]; teams: Team[] };

export const MessageEditor: React.FC<MessageEditorProps> = ({
    state,
    onChange,
    onFocusChange,
    post,
    plugin,
    isFocused,
    placeholder,
    mentions,
    teams,
}) => {
    const needParseMentions = React.useRef(false);

    const onStateChanged = React.useCallback(
        (state: EditorState) => {
            if (needParseMentions.current) {
                needParseMentions.current = false;
                state = insertMentions(state, mentions, teams);
            }
            onChange(state);
        },
        [mentions]
    );

    const onPaste = (): DraftHandleValue => {
        needParseMentions.current = true;
        return "not-handled";
    };

    const { extendedBlockRenderMap } = useAICompletion();
    const handleKeyCommand = useHandleKeyCommand();

    return (
        <AdvancedEditor
            state={state}
            isFocused={isFocused}
            onChange={onStateChanged}
            onFocusChange={onFocusChange}
            post={post}
            plugin={plugin}
            placeholder={placeholder}
            handleKeyCommand={handleKeyCommand}
            blockRenderMap={extendedBlockRenderMap}
            handlePastedText={onPaste}
            handleBeforeInput={handleBeforeInput}
        />
    );
};

type Props = Pick<MessageEditorProps, "plugin" | "placeholder">;
export const MessageEditorKnockout: React.FC<Props> = ({ plugin, placeholder }) => {
    const vm = useMessageContext();
    const entity = useProjectOrTask();
    const mentions = useSelector((state: RootMessageBoardState) => getMentions(state, entity));
    const teams = useSelector((state: RootMessageBoardState) => getTeamMentions(state, entity));
    const [state] = useKnockoutObservable(vm.editorState);
    const [isFocused] = useKnockoutObservable(vm.isFocused);

    const onChange = React.useCallback(newState => {
        vm.editorState(newState);
    }, []);

    const onFocusChange = React.useCallback(value => {
        vm.isFocused(value);
    }, []);

    const post = React.useCallback(() => vm.post(), []);

    return (
        <MessageEditor
            state={state}
            isFocused={isFocused}
            onChange={onChange}
            onFocusChange={onFocusChange}
            post={post}
            plugin={plugin}
            placeholder={placeholder}
            teams={teams}
            mentions={mentions}
        />
    );
};
